import { Box } from '@material-ui/core'
import { useOrderStore } from 'lib/hooks'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { OrderArticle } from 'shared'

export interface PickingButtonProps {
  type: string
  article: OrderArticle
  fullWidth?: boolean
  padding?: string
  handlePick?: (article: OrderArticle, count: number) => void
  handleCompletePicking?: (article: OrderArticle, readonly: boolean) => void
  handleReportIssue?: (article: OrderArticle) => void
  handleBackToPicking?: (article: OrderArticle) => void
  handleIssues?: (article: OrderArticle) => void
}

export const PickingButton = memo(
  ({
    type,
    article,
    handleCompletePicking,
    handleReportIssue,
    handleBackToPicking,
    handleIssues,
    fullWidth,
  }: PickingButtonProps): JSX.Element => {
    const { t } = useTranslation()
    const { disablePickingButtons } = useOrderStore()

    const getClickHandler = (article: OrderArticle) => {
      if (!disablePickingButtons) {
        switch (type) {
          case 'pick':
            return handleCompletePicking!(article, false)
          case 'picked':
            return handleCompletePicking!(article, true)
          case 'reportIssue':
            return handleReportIssue?.(article)
          case 'backToPicking':
            return handleBackToPicking?.(article)
          case 'issues':
            return handleIssues?.(article)
          default:
            break
        }
      }
    }

    const getButtonBody = () => {
      const icon = <i className="fa-solid fa-spinner fa-spin-pulse"></i>
      switch (type) {
        case 'pick':
          return (
            <span
              className={`picking-button pick-button ${fullWidth ? 'fullwidth' : ''} ${
                disablePickingButtons ? 'disable' : ''
              }`}
            >
              {disablePickingButtons && icon} {t('orders.picking.pick')}{' '}
            </span>
          )
        case 'picked':
          return (
            <span
              className={`picking-button picked-button ${fullWidth ? 'fullwidth' : ''} ${
                disablePickingButtons ? 'disable' : ''
              }`}
            >
              {disablePickingButtons && icon} Picked
            </span>
          )
        case 'reportIssue':
          return (
            <span
              className={`picking-button report-issue-button ${fullWidth ? 'fullwidth' : ''} ${
                disablePickingButtons ? 'disable' : ''
              }`}
            >
              {disablePickingButtons && icon} {t('orders.reportIssue')}
            </span>
          )
        case 'backToPicking':
          return (
            <span
              className={`picking-button back-to-pick-button ${fullWidth ? 'fullwidth' : ''} ${
                disablePickingButtons ? 'disable' : ''
              }`}
            >
              {disablePickingButtons && icon} {t('orders.backToPicking')}
            </span>
          )
        case 'issues':
          return (
            <span
              className={`picking-button issues-button ${fullWidth ? 'fullwidth' : ''} ${
                disablePickingButtons ? 'disable' : ''
              }`}
            >
              {disablePickingButtons && icon} {t('orders.issues')}
            </span>
          )
        default:
          return null
      }
    }

    return (
      <div onClick={() => getClickHandler(article)}>
        <Box textAlign="center">{getButtonBody()}</Box>
      </div>
    )
  }
)
